/**
 * useResolvedCollection
 *
 * Resolves the `:collectionId` route param against the shared collections list
 * for a collection page, and guards that the collection exists, is loaded, and
 * matches the type the page expects. Each collection page lives on a
 * type-specific route (e.g. /articles/collection/pr/:id), so this also catches
 * a mismatched link (a PR id on the RS route) and treats it as not-found.
 *
 * While collections are still loading the hook reports `status: "loading"`.
 * When the id is missing/invalid, the collection is absent, or its type does
 * not match, it reports `status: "not-found"`; pages use that to redirect away.
 */

import { useMemo } from "react"
import { useParams } from "react-router-dom"
import { useJournalArticles } from "@/components/journal-articles/JournalArticlesContext"
import { BiblioCollection } from "@/services/biblioService"

/**
 * The outcome of resolving a collection page's id param.
 */
export type ResolvedCollection =
  | { status: "loading" }
  | { status: "not-found" }
  | { status: "ready"; collection: BiblioCollection }

/**
 * Resolve the current route's collection id to a collection of the expected
 * type.
 *
 * @param expectedType - The `collectionType` the page requires (e.g.
 *   PR_LITERATURE_REVIEW_TYPE). A collection of a different type resolves to
 *   "not-found" so the page can redirect.
 * @returns The resolution status and, when ready, the matched collection.
 */
export function useResolvedCollection(expectedType: string): ResolvedCollection {
  const { collectionId: collectionIdParam } = useParams<{ collectionId: string }>()
  const { collections, collectionsLoading } = useJournalArticles()

  return useMemo((): ResolvedCollection => {
    // Still loading the collections list — cannot decide yet.
    if (collectionsLoading) {
      return { status: "loading" }
    }

    // Missing or non-numeric id.
    if (collectionIdParam === undefined) {
      return { status: "not-found" }
    }
    const parsedId: number = parseInt(collectionIdParam, 10)
    if (Number.isNaN(parsedId)) {
      return { status: "not-found" }
    }

    // No such collection, or wrong type for this page's route.
    const match: BiblioCollection | undefined = collections.find((c) => c.id === parsedId)
    if (match === undefined || match.collectionType !== expectedType) {
      return { status: "not-found" }
    }

    return { status: "ready", collection: match }
  }, [collectionsLoading, collectionIdParam, collections, expectedType])
}
